import { ThemeName } from '@design/design-tokens/themes/themes';
import { useWindowResize } from '@helpers';
import clsx from 'clsx';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useRef, useState } from 'react';

import styles from './PieChart.module.scss';
import { createPieSeries, getColors } from './utils';

export type PieChartData = {
  value: number;
  name: string;
}[];
export interface PieChartProps {
  data: PieChartData;
  theme?: ThemeName;
}

export const PieChart = ({ data, theme = 'dark_default' }: PieChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { normalColors } = getColors(theme);

  const onLegendEnter = (i: number) => {
    setActiveIndex(i);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: i,
      });
    }
  };

  const onLegendLeave = (i: number) => {
    setActiveIndex(null);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: i });
    }
  };

  useEffect(() => {
    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const chart = init(ref.current, theme);
    chart.setOption({
      legend: { show: false },
      series: createPieSeries(data, theme),
    });

    chart.on('mouseover', params => {
      if (params.componentType === 'series') {
        setActiveIndex(params.dataIndex);
      }
    });
    chart.on('mouseout', () => {
      setActiveIndex(null);
    });

    return () => {
      chart.dispose();
    };
  }, [data, theme]);

  return (
    <div className={styles.root} data-pie-chart-root>
      <div className={styles.chartWrapper} data-pie-chart-canvas ref={ref} />
      <div className={styles.legendWrapper} data-pie-chart-legend>
        {data.map((item, idx) => (
          <div
            className={clsx(styles.row, {
              [styles.rowActive]: activeIndex === idx,
            })}
            onMouseEnter={() => onLegendEnter(idx)}
            onMouseLeave={() => onLegendLeave(idx)}
            key={`row-${idx}`}
          >
            <div className={styles.legendNameWrapper}>
              <div
                className={styles.styledDot}
                style={{
                  backgroundColor: normalColors[idx % normalColors.length],
                }}
              />
              <div className={styles.bodySmallText}>{item.name}</div>
            </div>
            <div className={styles.bodySmallText}>
              {(item.value * 100).toFixed(2) + '%'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
